import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DialogContent } from "@material-ui/core";
import { DialogAnimate } from "src/components/Animate";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "src/utils/axios";
import SignaturePad from "./SignaturePad";

dayjs.extend(utc);

const fromUtc = (d) => (d ? dayjs.utc(d).local() : null);

// Códigos cerrados que acepta el backend.
const REASON_CODES = [
  { value: "FORGOT_IN", label: "Olvidé fichar la entrada" },
  { value: "FORGOT_OUT", label: "Olvidé fichar la salida" },
  { value: "SYSTEM_FAILURE", label: "Fallo del sistema / impresora" },
  { value: "AGREED_CHANGE", label: "Cambio acordado con el encargado" },
  { value: "OTHER", label: "Otro motivo" },
];

const MODES = [
  { value: "close", label: "Cerrar olvidado", icon: "✕", desc: "Olvidé fichar la salida" },
  { value: "edit", label: "Editar horas", icon: "✎", desc: "Las horas registradas no son correctas" },
  { value: "new", label: "Crear retroactivo", icon: "+", desc: "Olvidé fichar entrada Y salida" },
];

// Convierte "HH:mm" sobre una fecha base a ISO UTC (`...Z`).
// El empleado piensa en hora LOCAL del restaurante (Madrid). dayjs sin .utc()
// usa la TZ del navegador (en el TPV del local = Madrid), por lo que componer
// con .hour()/.minute() crea un instante local; .toISOString() lo convierte
// a UTC, que es como se guardan todos los fichajes del sistema.
// Antes mandábamos `YYYY-MM-DDTHH:mm:ss` sin TZ: el backend lo guardaba como
// wall-clock 14:30 y el front al releer con `dayjs.utc(...).local()` sumaba
// +2h en CEST → la propuesta aparecía como 16:30.
const composeUtcIso = (baseDate, hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const d = dayjs(baseDate).hour(h).minute(m).second(0).millisecond(0);
  return d.toISOString();
};

/**
 * Dialog para que el empleado proponga una corrección a su fichaje.
 * Se invoca desde EmployeeShiftTracker con el contexto del workItem actual:
 *   - `workItem`: WorkScheduleItem en foco (id, start, end, shifts).
 *   - `employee`: empleado autenticado en el kiosko (id, name).
 *
 * El dialog muestra 3 modos y, según el seleccionado, distintos campos. Al
 * enviar dispara el endpoint correspondiente con motivo + firma del empleado.
 * El admin recibirá la propuesta en el AlertsCenter / vista dedicada.
 */
export default function EmployeeCorrectionDialog({ open, onClose, workItem, employee }) {
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState("close");
  const [reasonCode, setReasonCode] = useState("FORGOT_OUT");
  const [reasonText, setReasonText] = useState("");
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Para "close": elegir el shift abierto del workItem.
  const openShift = useMemo(
    () => (workItem?.shifts || []).find((s) => s.checkInTime && !s.checkOutTime),
    [workItem],
  );
  // Para "edit": elegir cualquiera de los shifts del workItem.
  const allShifts = workItem?.shifts || [];
  const [editingShiftId, setEditingShiftId] = useState(
    openShift?.id || allShifts[0]?.id || null,
  );
  const editingShift = allShifts.find((s) => s.id === editingShiftId);

  // Estado de tiempos por modo.
  const baseDay = workItem?.start ? fromUtc(workItem.start) : dayjs();
  const initialIn = workItem?.start ? fromUtc(workItem.start).format("HH:mm") : "09:00";
  const initialOut = workItem?.end ? fromUtc(workItem.end).format("HH:mm") : "13:00";

  const [closeHHMM, setCloseHHMM] = useState(initialOut);
  const [editInHHMM, setEditInHHMM] = useState(
    editingShift?.checkInTime ? fromUtc(editingShift.checkInTime).format("HH:mm") : initialIn,
  );
  const [editOutHHMM, setEditOutHHMM] = useState(
    editingShift?.checkOutTime ? fromUtc(editingShift.checkOutTime).format("HH:mm") : initialOut,
  );
  const [newInHHMM, setNewInHHMM] = useState(initialIn);
  const [newOutHHMM, setNewOutHHMM] = useState(initialOut);

  // Sync edit-mode times cuando el usuario cambia el shift en el selector.
  React.useEffect(() => {
    if (mode !== "edit" || !editingShift) return;
    setEditInHHMM(
      editingShift.checkInTime
        ? fromUtc(editingShift.checkInTime).format("HH:mm")
        : initialIn,
    );
    setEditOutHHMM(
      editingShift.checkOutTime
        ? fromUtc(editingShift.checkOutTime).format("HH:mm")
        : initialOut,
    );
  }, [editingShiftId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validación del payload mínima — el backend valida estricto, esto es UX.
  const validate = () => {
    if (!signature || signature.length < 200)
      return "Firma con tu dedo o ratón antes de enviar.";
    if (reasonCode === "OTHER" && (!reasonText || reasonText.trim().length < 15))
      return "Explica el motivo (mínimo 15 caracteres).";

    if (mode === "close") {
      if (!openShift) return "No hay ningún turno abierto que cerrar.";
      if (!closeHHMM) return "Indica la hora de salida.";
    }
    if (mode === "edit") {
      if (!editingShift) return "Selecciona un turno a editar.";
      if (!editInHHMM && !editOutHHMM) return "Cambia al menos una de las horas.";
    }
    if (mode === "new") {
      if (!newInHHMM || !newOutHHMM) return "Indica entrada y salida.";
      if (newInHHMM >= newOutHHMM) return "La salida debe ser posterior a la entrada.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      enqueueSnackbar(err, { variant: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "close") {
        await axios.post("/clocking-adjustment/employee-request/close-forgotten", {
          employeeId: employee.id,
          shiftTrackerId: openShift.id,
          checkOutTime: composeUtcIso(baseDay, closeHHMM),
          reasonCode,
          reasonText,
          employeeSignature: signature,
        });
      } else if (mode === "edit") {
        await axios.post("/clocking-adjustment/employee-request/correction", {
          employeeId: employee.id,
          shiftTrackerId: editingShift.id,
          newStart: composeUtcIso(baseDay, editInHHMM),
          newEnd: editOutHHMM ? composeUtcIso(baseDay, editOutHHMM) : null,
          reasonCode,
          reasonText,
          employeeSignature: signature,
        });
      } else if (mode === "new") {
        await axios.post("/clocking-adjustment/employee-request/new-shift", {
          employeeId: employee.id,
          workScheduleItemId: workItem.id,
          checkInTime: composeUtcIso(baseDay, newInHHMM),
          checkOutTime: composeUtcIso(baseDay, newOutHHMM),
          reasonCode,
          reasonText,
          employeeSignature: signature,
        });
      }
      enqueueSnackbar("Solicitud enviada. El administrador la revisará.", {
        variant: "success",
      });
      onClose?.();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data || "No se pudo enviar la solicitud.",
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Disponibilidad de cada modo según el estado del workItem.
  const canClose = !!openShift;
  const canEdit = allShifts.length > 0;
  // El modo "new" es válido sólo si no hay shifts en el workItem.
  const canNew = allShifts.length === 0;

  // Si el modo seleccionado deja de ser válido, conmutar al primero disponible.
  React.useEffect(() => {
    const validNow =
      (mode === "close" && canClose) ||
      (mode === "edit" && canEdit) ||
      (mode === "new" && canNew);
    if (!validNow) {
      if (canClose) setMode("close");
      else if (canEdit) setMode("edit");
      else if (canNew) setMode("new");
    }
  }, [mode, canClose, canEdit, canNew]);

  return (
    <DialogAnimate open={open} onClose={onClose} width="md">
      <DialogContent style={{ padding: 24 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Solicitar corrección de fichaje
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {employee?.name} · {baseDay?.format("dddd D MMM")}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ py: 0.5 }}>
            Tu propuesta se enviará al administrador para que la apruebe. El
            fichaje no se modifica hasta entonces.
          </Alert>

          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
              ¿Qué quieres corregir?
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              fullWidth
              size="small"
              sx={{ flexWrap: "wrap" }}
            >
              {MODES.map((m) => {
                const enabled =
                  (m.value === "close" && canClose) ||
                  (m.value === "edit" && canEdit) ||
                  (m.value === "new" && canNew);
                return (
                  <ToggleButton
                    key={m.value}
                    value={m.value}
                    disabled={!enabled}
                    sx={{ flexDirection: "column", py: 1, lineHeight: 1.2 }}
                  >
                    <Box sx={{ fontSize: 18, fontWeight: 800 }}>{m.icon}</Box>
                    <Box sx={{ fontSize: 11, fontWeight: 700 }}>{m.label}</Box>
                    <Box sx={{ fontSize: 10, color: "text.secondary", textTransform: "none" }}>
                      {m.desc}
                    </Box>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>

          {/* ── Modo: cerrar olvidado ── */}
          {mode === "close" && (
            <Box>
              {openShift ? (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Entrada registrada: {fromUtc(openShift.checkInTime)?.format("HH:mm")}
                  </Typography>
                  <TextField
                    fullWidth
                    type="time"
                    size="small"
                    label="Hora real de salida"
                    value={closeHHMM}
                    onChange={(e) => setCloseHHMM(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mt: 1 }}
                  />
                </>
              ) : (
                <Alert severity="warning">
                  No tienes ningún turno abierto en este horario.
                </Alert>
              )}
            </Box>
          )}

          {/* ── Modo: editar horas ── */}
          {mode === "edit" && (
            <Box>
              {allShifts.length > 1 && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Turno a editar"
                  value={editingShiftId ?? ""}
                  onChange={(e) => setEditingShiftId(Number(e.target.value))}
                  sx={{ mb: 1.5 }}
                >
                  {allShifts.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {fromUtc(s.checkInTime)?.format("HH:mm")} →{" "}
                      {s.checkOutTime ? fromUtc(s.checkOutTime).format("HH:mm") : "abierto"}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Stack direction="row" spacing={1.5}>
                <TextField
                  type="time"
                  size="small"
                  label="Nueva entrada"
                  value={editInHHMM}
                  onChange={(e) => setEditInHHMM(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="time"
                  size="small"
                  label="Nueva salida"
                  value={editOutHHMM}
                  onChange={(e) => setEditOutHHMM(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              {editingShift && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Actual:{" "}
                  <Chip
                    size="small"
                    label={`${fromUtc(editingShift.checkInTime)?.format("HH:mm")} → ${
                      editingShift.checkOutTime
                        ? fromUtc(editingShift.checkOutTime).format("HH:mm")
                        : "abierto"
                    }`}
                    sx={{ height: 18, fontSize: 10 }}
                  />
                </Typography>
              )}
            </Box>
          )}

          {/* ── Modo: crear retroactivo ── */}
          {mode === "new" && (
            <Box>
              {allShifts.length > 0 ? (
                <Alert severity="warning">
                  Ya hay fichajes en este turno. Usa "Editar horas" en su lugar.
                </Alert>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Horario previsto: {baseDay?.format("HH:mm")} —{" "}
                    {fromUtc(workItem?.end)?.format("HH:mm")}
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                    <TextField
                      type="time"
                      size="small"
                      label="Entrada"
                      value={newInHHMM}
                      onChange={(e) => setNewInHHMM(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      type="time"
                      size="small"
                      label="Salida"
                      value={newOutHHMM}
                      onChange={(e) => setNewOutHHMM(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Stack>
                </>
              )}
            </Box>
          )}

          {/* ── Motivo (todos los modos) ── */}
          <TextField
            select
            fullWidth
            size="small"
            label="Motivo"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
          >
            {REASON_CODES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          {reasonCode === "OTHER" && (
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Explica el motivo (mínimo 15 caracteres)"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
            />
          )}

          {/* ── Firma ── */}
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
              Firma del trabajador (obligatorio)
            </Typography>
            <SignaturePad onChange={setSignature} />
          </Box>

          {/* ── Botones ── */}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <LoadingButton
              variant="contained"
              loading={submitting}
              onClick={handleSubmit}
            >
              Enviar solicitud
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </DialogAnimate>
  );
}
